/**
 * DocumentModalHeader — top header bar of DocumentModal.
 *
 * Extracted from DocumentModal.tsx to reduce render complexity.
 * Contains both desktop and mobile (compact) variants, including:
 * - Document title, status badge, doc number
 * - Copy share link, parent document link, viewers badge
 * - Print, Save, Final Action dropdown
 * - Make Recurring button (desktop)
 * - Side panel toggle (desktop)
 * - Mobile collapsed header summary
 *
 * Also contains the final action menu rendering helpers
 * (getFinalActionToneColor, getFinalActionIcon, handleFinalActionClick,
 * renderConfigFinalActionMenuItems) moved from the parent.
 */

import React from 'react';
import {
  X, FileText, Hash, Link2, ExternalLink, Users, ChevronDown,
  Printer, Save, Check, RefreshCw, ArrowRight, Loader2,
  Eye, Send, FileCheck, Receipt, AlertTriangle, CheckCircle2, XCircle,
  Mail, MessageCircle, CheckSquare, Zap, Cloud, Globe, Layers,
  type LucideIcon,
} from 'lucide-react';
import { getDinLineLinkGaps } from '@/lib/finance/expenseAccountMapping';
import {
  type DocumentConfig,
  type DocumentFormState,
  type FinalActionMenuItem,
  type FinalActionMenuConfig,
} from '../documentModalHelpers';
import {
  type Document,
  type DocumentType,
  type DocumentLineItem,
  type DocumentStatus,
} from '@/hooks/useDocumentsWorkItems';
import { type TaskViewer } from '@/hooks/useTaskViewerTracking';
import { FinanceStatusBadge } from '../DocumentModalShared';
import SidePanelToggle, { type SidePanelMode } from '../SidePanelToggle';
import { type UserAction as LifecycleAction } from '@/lib/finance/documentLifecycle';
import { resolveDocumentOrigin, type DocumentSourceMeta } from '@/lib/finance/documentSource';
import { type ConnectedAdapter } from '@/hooks/useConnectedAdapters';
import { AdapterLogo } from '@/app/client-zone/modules/total-finance/components/AdapterLogo';
import { ExtractionSourceBadge } from '@/components/finance/ExtractionSourceBadge';
import UserDocsLink from '@/components/shared/UserDocsLink';

const DOCUMENT_SOURCE_ICONS: Record<DocumentSourceMeta['icon'], LucideIcon> = {
  mail: Mail,
  'message-circle': MessageCircle,
  'check-square': CheckSquare,
  zap: Zap,
  link: Link2,
  cloud: Cloud,
  globe: Globe,
};

export interface PromotionTarget {
  type: DocumentType;
  label: string;
  icon: string;
  description?: string;
}

export interface DocumentModalHeaderProps {
  // Visual
  primaryColor: string;
  isDarkMode: boolean;
  isCompactMobileDoc: boolean;

  // Document data
  document: Document | null | undefined;
  doc_type: DocumentType;
  formData: DocumentFormState;
  config: DocumentConfig;
  items: DocumentLineItem[];
  /** Client-level inventory movement tracking — gates the stock-link requirement. */
  inventoryTrackingEnabled?: boolean;

  // Mobile-specific
  mobileHeaderTitle: string;
  mobileHeaderSummary: string;
  isMobileHeaderExpanded: boolean;
  setIsMobileHeaderExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  activeTab: 'items' | 'details' | 'notes' | 'attachments' | 'approvals';
  setActiveTab: React.Dispatch<React.SetStateAction<'items' | 'details' | 'notes' | 'attachments' | 'approvals'>>;
  currencySymbol: string;
  formatCurrency: (value: number | string | undefined | null) => string;

  // Parent document
  sourceJobNumber: string | null;
  linkedParentDocument: Document | null;

  // Viewers
  otherDocViewersCount: number;
  docViewers: TaskViewer[];

  // Share link
  docShareUrl: string | null;
  docShareUrlLoading: boolean;
  handleDocShareLinkAction: () => Promise<void>;

  // Save
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  isBatchedInvoiceLocked: boolean;
  lastAutoSave: Date | null;
  handleSaveWithRiskCheck: (autoSave: boolean) => Promise<void>;

  // Print
  handleCompleteAndPreview: () => Promise<void>;

  // Final action menu
  showPromotionMenu: boolean;
  setShowPromotionMenu: React.Dispatch<React.SetStateAction<boolean>>;
  isPromoting: boolean;
  isFinalActionMenuGated: boolean;
  finalActionGateResult: { pass: boolean; message?: string };
  finalActionMenuConfig: FinalActionMenuConfig | undefined;
  finalActionSourceDoc: Partial<Document>;

  // Promotion
  hasExpensePromotionTarget: boolean;
  canPromote: boolean;
  promotionTargets: PromotionTarget[];
  handleCompleteAndSendConversion: () => Promise<void>;
  handleOpenSendModal: () => void;
  handlePromoteDocument: (targetType: DocumentType) => Promise<void>;

  // Lifecycle actions
  lifecycleActions: LifecycleAction[];
  performingActionId: string | null;
  performLifecycleAction: (action: LifecycleAction) => Promise<void>;

  // External publish — "Publish to {Provider}" items in the final action dropdown
  connectedAdapters: ConnectedAdapter[];
  publishingToExternal: boolean;
  onPublishToExternal: (adapter: ConnectedAdapter) => Promise<void>;

  // Approve & Publish — standard mode DIN → expense → approve → auto-publish
  workflowMode: string;
  onApproveAndPublish: () => Promise<void>;

  // Save as Draft — DIN-only: saves partial progress (e.g., GL allocated but
  // no supplier) with status='draft' and closes the modal.
  onSaveAsDraft?: () => Promise<void>;
  isSavingDraft?: boolean;

  // Recurring
  setShowRecurringModal: React.Dispatch<React.SetStateAction<boolean>>;

  // Close
  handleCloseModal: () => void;

  // Side panel
  sidePanelMode: SidePanelMode;
  setSidePanelMode: React.Dispatch<React.SetStateAction<SidePanelMode>>;
  shouldShowContextualAssistant: boolean;
}

export function DocumentModalHeader({
  primaryColor,
  isDarkMode,
  isCompactMobileDoc,
  document,
  doc_type,
  formData,
  config,
  items,
  inventoryTrackingEnabled = true,
  mobileHeaderTitle,
  mobileHeaderSummary,
  isMobileHeaderExpanded,
  setIsMobileHeaderExpanded,
  activeTab,
  setActiveTab,
  currencySymbol,
  formatCurrency,
  sourceJobNumber,
  linkedParentDocument,
  otherDocViewersCount,
  docViewers,
  docShareUrl,
  docShareUrlLoading,
  handleDocShareLinkAction,
  isSaving,
  hasUnsavedChanges,
  isBatchedInvoiceLocked,
  lastAutoSave,
  handleSaveWithRiskCheck,
  handleCompleteAndPreview,
  showPromotionMenu,
  setShowPromotionMenu,
  isPromoting,
  isFinalActionMenuGated,
  finalActionGateResult,
  finalActionMenuConfig,
  finalActionSourceDoc,
  hasExpensePromotionTarget,
  canPromote,
  promotionTargets,
  handleCompleteAndSendConversion,
  handleOpenSendModal,
  handlePromoteDocument,
  lifecycleActions,
  performingActionId,
  performLifecycleAction,
  connectedAdapters,
  publishingToExternal,
  onPublishToExternal,
  workflowMode,
  onApproveAndPublish,
  onSaveAsDraft,
  isSavingDraft,
  setShowRecurringModal,
  handleCloseModal,
  sidePanelMode,
  setSidePanelMode,
  shouldShowContextualAssistant: _shouldShowContextualAssistant,
}: DocumentModalHeaderProps) {
  // ==========================================================================
  // FINAL ACTION MENU HELPERS — moved from parent
  // ==========================================================================

  const documentSourceMeta = resolveDocumentOrigin(document?.source ?? null, document?.custom_fields ?? null);

  const renderSourceBadge = () => {
    if (!documentSourceMeta) return null;
    const SourceIcon = DOCUMENT_SOURCE_ICONS[documentSourceMeta.icon];
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${documentSourceMeta.badgeClassName}`}
        title={`Source: ${documentSourceMeta.label}`}
      >
        {SourceIcon && <SourceIcon className="h-3 w-3" />}
        {documentSourceMeta.label}
      </span>
    );
  };

  const getFinalActionToneColor = (tone?: string) => {
    switch (tone) {
      case 'success':
        return 'text-green-600';
      case 'danger':
        return 'text-red-600';
      case 'warning':
        return 'text-amber-600';
      case 'primary':
      default:
        return 'text-blue-600';
    }
  };

  const getFinalActionIcon = (item: FinalActionMenuItem) => {
    const colorClass = getFinalActionToneColor(item.tone);
    if (item.icon) {
      const Icon = item.icon;
      return <Icon size={16} className={colorClass} />;
    }
    switch (item.type) {
      case 'preview':
        return <Printer size={16} className={colorClass} />;
      case 'send':
        return <Send size={16} className={colorClass} />;
      case 'complete':
        return <FileCheck size={16} className={colorClass} />;
      case 'convert':
        return <Receipt size={16} className={colorClass} />;
      case 'recurring':
        return <RefreshCw size={16} className={colorClass} />;
      case 'add_to_batch':
        return <Layers size={16} className={colorClass} />;
      default:
        return null;
    }
  };

  // Build a list of short labels for all outstanding final-action requirements.
  // This mirrors the pattern used by ServiceJobModal / AppointmentModal so the
  // reviewer can see exactly what is blocking the "Create Expense" button
  // without having to hover or open the dropdown.  Only shown for document
  // types that have a finalActionMenu gate (DIN, expense, supplier_invoice).
  const outstandingRequirements = React.useMemo<string[]>(() => {
    if (!isFinalActionMenuGated) return [];
    const reqs: string[] = [];
    const meaningfulItems = items.filter((item) => {
      const lineType = item.line_type || 'item';
      if (lineType === 'spacer' || lineType === 'header' || lineType === 'comment' || lineType === 'subtotal') return false;
      const description = item.description?.trim();
      const qty = Number(item.quantity || 0);
      const total = Number(item.line_total || 0);
      return Boolean(description) || qty !== 0 || total !== 0;
    });

    // Employee reimbursement claims (mileage, per-diem) are contactless by
    // design — don't list "Select Supplier" as an outstanding requirement.
    const isEmployeeReimbursementClaim =
      doc_type === 'din'
      && typeof document?.custom_fields?.reimbursement_route === 'string';
    if (!formData.contact_id && !isEmployeeReimbursementClaim) reqs.push('Select Supplier');
    if (meaningfulItems.length === 0) {
      reqs.push('Add Line Item');
    } else {
      const missingGL = meaningfulItems.filter((item) => !item.expense_account_code?.trim()).length;
      const missingAlloc = meaningfulItems.filter((item) => !item.allocation_type?.trim()).length;
      const linkGaps = meaningfulItems.map((item) => getDinLineLinkGaps(item, { inventoryTrackingEnabled }));
      // Customer + job is compulsory only for billable lines (QBO parity —
      // the Customer column is optional on non-billable expense lines).
      const missingBillable = linkGaps.filter((g) => g.missingBillableCustomerJob).length;
      const missingStock = linkGaps.filter((g) => g.missingStockItem).length;
      if (missingGL > 0) reqs.push(missingGL === 1 ? 'Allocate GL' : `Allocate GL (${missingGL} lines)`);
      if (missingAlloc > 0) reqs.push(missingAlloc === 1 ? 'Set Allocation' : `Set Allocation (${missingAlloc} lines)`);
      if (missingBillable > 0) reqs.push(missingBillable === 1 ? 'Link Customer & Job (billable)' : `Link Customer & Job (${missingBillable} billable lines)`);
      if (missingStock > 0) reqs.push(missingStock === 1 ? 'Link Stock' : `Link Stock (${missingStock} lines)`);
    }
    // Balancing-line check — the gate result message tells us if there's a discrepancy
    if (finalActionGateResult.message?.toLowerCase().includes('balancing') || finalActionGateResult.message?.toLowerCase().includes('slip total')) {
      reqs.push('Add Balancing Line');
    }
    return reqs.slice(0, 5);
  }, [isFinalActionMenuGated, items, formData.contact_id, doc_type, document?.custom_fields, finalActionGateResult.message, inventoryTrackingEnabled]);

  // Compute the primary action label for the dropdown toggle button.
  // When the gate passes, show the first non-preview action label instead
  // of the generic "Actions" text.
  const primaryActionLabel = React.useMemo(() => {
    if (!finalActionMenuConfig) return undefined;
    const primaryItem = finalActionMenuConfig.items.find(
      (item) => item.type !== 'preview' && (!item.condition || item.condition(finalActionSourceDoc, items))
    );
    return primaryItem?.label;
  }, [finalActionMenuConfig, finalActionSourceDoc, items]);

  const handleFinalActionClick = (item: FinalActionMenuItem) => {
    if (isFinalActionMenuGated) return;
    setShowPromotionMenu(false);
    // DIN documents always materialize into an expense via handlePromoteDocument,
    // regardless of whether the YAML promotion-target registry has initialized.
    // Falling through to handleCompleteAndPreview opens a preview overlay that
    // requires an extra "send" step — the final action button should be final.
    const isDin = doc_type === 'din';
    switch (item.type) {
      case 'preview':
        void handleCompleteAndPreview();
        break;
      case 'send':
        void (isDin || hasExpensePromotionTarget ? handleCompleteAndSendConversion() : handleOpenSendModal());
        break;
      case 'complete':
        void (isDin || hasExpensePromotionTarget ? handlePromoteDocument('expense') : handleCompleteAndPreview());
        break;
      case 'submit_for_approval':
        if (isDin || hasExpensePromotionTarget) {
          // Materialize the DIN into an expense, then submit for approval.
          // In strict/simplified mode, the materialize endpoint creates the
          // expense with 'pending'/'approved' status based on workflow mode.
          void handlePromoteDocument('expense');
        } else {
          // Non-DIN docs (invoice/credit_note/etc): lifecycle submit → pending_approval
          void performLifecycleAction({
            id: 'submit',
            label: item.label,
            targetStatus: 'pending_approval',
            tone: 'primary',
          });
        }
        break;
      case 'add_to_batch': {
        // Open the invoice-batches drawer with this document preselected.
        const docId = document?.id || formData.id;
        if (!docId || typeof window === 'undefined') break;
        window.dispatchEvent(
          new CustomEvent('openInvoiceBatchesDrawer', {
            detail: { preselectWorkItemIds: [docId] },
          })
        );
        break;
      }
      case 'convert':
        if (item.targetType) void handlePromoteDocument(item.targetType);
        break;
      case 'recurring':
        setShowRecurringModal(true);
        break;
    }
  };

  const renderConfigFinalActionMenuItems = () => {
    if (!finalActionMenuConfig) return null;
    return finalActionMenuConfig.items
      .filter((item) => !item.condition || item.condition(finalActionSourceDoc, items))
      .map((item) => {
        const itemDisabled =
          isFinalActionMenuGated || (item.disabled?.(finalActionSourceDoc, items) ?? false);
        return (
          <button
            key={`${item.type}-${item.label}`}
            type="button"
            onClick={() => !itemDisabled && handleFinalActionClick(item)}
            disabled={itemDisabled}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50"
          >
            {getFinalActionIcon(item)}
            <span className="leading-tight">{item.label}</span>
          </button>
        );
      });
  };

  /**
   * Render "Publish to {Provider}" items for each connected external adapter.
   * Only shown when:
   *   - The document has been saved (has an ID)
   *   - The document type is publishable (expense, supplier_invoice, invoice, etc.)
   *   - The document status is 'approved' (ready to publish) or already has
   *     an external_id (allowing re-sync)
   */
  const PUBLISHABLE_TYPES = new Set([
    'invoice', 'quote', 'proforma', 'credit_note',
    'supplier_invoice', 'expense', 'purchase_order',
  ]);
  const docItemType = String(document?.item_type || doc_type || '');
  const docStatus = String(formData.status || document?.status || '');
  const docExternalId = (document as Record<string, unknown> | undefined)?.external_id as string | null | undefined;
  const isPublishableDocType = PUBLISHABLE_TYPES.has(docItemType);
  const canShowPublishItems = Boolean(
    document?.id
    && isPublishableDocType
    && connectedAdapters.length > 0
    && (docStatus === 'approved' || docExternalId)
  );

  const renderPublishMenuItems = () => {
    if (!canShowPublishItems) return null;
    return (
      <>
        <div className="mx-2 border-t border-gray-100" />
        <div className="px-3 py-2">
          <span className="text-[10px] font-semibold uppercase text-gray-500">Publish</span>
        </div>
        {connectedAdapters.map((adapter) => (
          <button
            key={`publish-${adapter.config.id}`}
            type="button"
            onClick={() => void onPublishToExternal(adapter)}
            disabled={publishingToExternal || performingActionId !== null}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50"
          >
            <AdapterLogo meta={adapter.meta} size={16} />
            <span className="leading-tight">
              {docExternalId ? 'Re-sync to ' : 'Publish to '}{adapter.meta.label}
            </span>
          </button>
        ))}
      </>
    );
  };

  /**
   * Render "Approve & Publish" item for DINs in standard workflow mode.
   * This materializes the DIN → creates the expense → approves it → the
   * server auto-publishes to the connected external adapter.
   *
   * Only shown when:
   *   - doc_type is 'din'
   *   - workflowMode is 'standard' (simplified auto-approves on materialize;
   *     strict goes through DocumentApprovalBoard)
   *   - At least one external adapter is connected
   *   - The final action gate passes (supplier + GL accounts allocated)
   *
   * In standard mode the materialize endpoint already creates the expense as
   * 'approved' and calls publishOnApproval, so this button is effectively a
   * shortcut that labels the same action differently for users who have an
   * external adapter connected.
   */
  const canShowApproveAndPublish = Boolean(
    doc_type === 'din'
    && workflowMode === 'standard'
    && connectedAdapters.length > 0
    && !isFinalActionMenuGated
  );

  const renderApproveAndPublishMenuItem = () => {
    if (!canShowApproveAndPublish) return null;
    return (
      <>
        <div className="mx-2 border-t border-gray-100" />
        <button
          type="button"
          onClick={() => void onApproveAndPublish()}
          disabled={isPromoting || publishingToExternal}
          className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50"
        >
          <CheckCircle2 size={16} className="text-green-600" />
          <span className="leading-tight">Approve & Publish</span>
        </button>
      </>
    );
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <>
      {/* Header - Solid Primary Color */}
      <div
        data-tour-id="document-modal-header"
        className={`${isCompactMobileDoc ? 'relative z-20 px-3 pt-2.5 pb-2' : 'px-3 sm:px-6 py-3 sm:py-5'} text-white flex-shrink-0`}
        style={{ backgroundColor: primaryColor }}
      >
        {isCompactMobileDoc ? (
          <div className="flex items-start justify-between gap-2.5">
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              <div className="rounded-xl bg-white/20 p-2 backdrop-blur">
                <FileText className="text-white" size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <h2 className="truncate text-sm font-bold">{mobileHeaderTitle}</h2>
                  <UserDocsLink docsPath="/total-operations/workflows/finance-essential/creating-documents" label="User guide: Creating documents" className="text-white/80 hover:text-white" />
                  {formData.status && <FinanceStatusBadge status={formData.status} isDark={true} itemType={doc_type} />}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-white/80">
                  {formData.doc_number ? (
                    <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-white/10 px-2 py-0.5">
                      <Hash size={10} />
                      <span className="truncate max-w-[9.5rem]">
                        {sourceJobNumber ? `${sourceJobNumber} → ${formData.doc_number}` : formData.doc_number}
                      </span>
                    </span>
                  ) : (
                    <span className="rounded-full bg-white/10 px-2 py-0.5">Draft in progress</span>
                  )}
                  {renderSourceBadge()}
                  <ExtractionSourceBadge customFields={document?.custom_fields ?? null} />
                </div>
                {outstandingRequirements.length > 0 && (
                  <div className="mt-1.5 flex items-center gap-1.5 rounded-lg bg-amber-500/30 px-2 py-1 text-[10px] font-medium text-amber-100 md:hidden">
                    <AlertTriangle size={11} className="shrink-0" />
                    <span className="truncate">{outstandingRequirements.join(' · ')}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1.5 pl-1">
              {/* 1. Copy share link */}
              <button
                onClick={() => void handleDocShareLinkAction()}
                disabled={docShareUrlLoading || !document?.id}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-70"
                title={docShareUrl ? 'Copy share link' : 'Generate and copy share link'}
              >
                {docShareUrlLoading ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Link2 size={15} />
                )}
              </button>

              {/* 2. Parent document # + link (if applicable) */}
              {linkedParentDocument && (
                <button
                  onClick={() => {
                    if (linkedParentDocument.id && typeof window !== 'undefined') {
                      window.dispatchEvent(
                        new CustomEvent('openDocumentModal', {
                          detail: {
                            doc_type: linkedParentDocument.doc_type,
                            mode: 'edit',
                            documentId: linkedParentDocument.id,
                          },
                        })
                      );
                    }
                  }}
                  className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
                  title={`Open parent: ${linkedParentDocument.doc_number || 'Parent Document'}`}
                >
                  <ExternalLink size={12} />
                  <span>{linkedParentDocument.doc_number || 'Parent'}</span>
                </button>
              )}

              {/* Other Viewers Badge */}
              {otherDocViewersCount > 0 && (
                <div
                  className="flex items-center gap-1 px-1.5 py-1 bg-amber-500/30 text-amber-100 rounded-lg text-[10px] font-medium animate-pulse"
                  title={docViewers.map(v => v.user_name).join(', ')}
                >
                  <Users className="w-3 h-3" />
                  <span>{otherDocViewersCount}</span>
                </div>
              )}
              <button
                onClick={() => setIsMobileHeaderExpanded(prev => !prev)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 text-white transition-colors hover:bg-white/25"
                title={isMobileHeaderExpanded ? 'Minimize header details' : 'Expand header details'}
                aria-label={isMobileHeaderExpanded ? 'Minimize header details' : 'Expand header details'}
              >
                <ChevronDown size={16} className={`transition-transform ${isMobileHeaderExpanded ? 'rotate-180' : ''}`} />
              </button>

              {/* 3. Print */}
              <button
                type="button"
                onClick={() => void handleCompleteAndPreview()}
                className="inline-flex h-9 items-center justify-center gap-1 rounded-xl bg-white/10 px-2.5 text-white transition-colors hover:bg-white/20"
                title="Print preview"
              >
                <Printer size={15} />
                <span className="truncate text-[10px] font-semibold leading-none">Print</span>
              </button>

              {/* 4. Save / Saving / Saved */}
              <button
                type="button"
                onClick={() => handleSaveWithRiskCheck(false)}
                disabled={isSaving || !hasUnsavedChanges || isBatchedInvoiceLocked}
                className={`inline-flex h-9 max-w-[5.5rem] items-center justify-center gap-1 rounded-xl px-2.5 shadow-sm transition-colors ${hasUnsavedChanges && !isBatchedInvoiceLocked
                  ? 'bg-white text-gray-800 hover:bg-gray-100'
                  : 'bg-white/20 text-white/60 cursor-not-allowed'
                  }`}
                title={
                  isBatchedInvoiceLocked
                    ? 'Document is locked by an open invoice batch. Post the batch or create a credit note to make changes.'
                    : isSaving
                      ? 'Saving…'
                      : !hasUnsavedChanges && lastAutoSave
                        ? 'Saved'
                        : 'Save'
                }
              >
                {isSaving ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : !hasUnsavedChanges && lastAutoSave ? (
                  <Check size={15} />
                ) : (
                  <Save size={15} />
                )}
                <span className="truncate text-[10px] font-semibold leading-none">
                  {isSaving
                    ? 'Saving'
                    : !hasUnsavedChanges && lastAutoSave
                      ? 'Saved'
                      : 'Save'}
                </span>
              </button>

              {/* 4b. Save as Draft — DIN-only: saves partial progress and closes */}
              {doc_type === 'din' && onSaveAsDraft && (
                <button
                  type="button"
                  onClick={() => void onSaveAsDraft()}
                  disabled={isSavingDraft || isSaving}
                  className="inline-flex h-9 items-center justify-center gap-1 rounded-xl bg-white/20 px-2.5 text-white transition-colors hover:bg-white/30 disabled:opacity-50"
                  title="Save partial progress as draft (e.g., GL allocated but no supplier) and close"
                >
                  {isSavingDraft ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <FileText size={15} />
                  )}
                  <span className="truncate text-[10px] font-semibold leading-none">Draft</span>
                </button>
              )}

              {/* 5. Final Action Dropdown Button */}
              <div className="relative shrink-0" data-promotion-menu>
                <button
                  data-tour-id="document-actions-menu"
                  onClick={() => setShowPromotionMenu(!showPromotionMenu)}
                  disabled={isPromoting || isBatchedInvoiceLocked || isFinalActionMenuGated}
                  className="inline-flex h-9 items-center justify-center gap-1 rounded-xl bg-white px-2.5 text-gray-900 shadow-sm transition-colors hover:bg-gray-100 disabled:opacity-50"
                  title={
                    isBatchedInvoiceLocked
                      ? 'Document is locked by an open invoice batch.'
                      : isFinalActionMenuGated
                        ? finalActionGateResult.message || 'Required fields missing'
                        : isPromoting
                          ? 'Working…'
                          : (primaryActionLabel || 'Document actions')
                  }
                  aria-label={isPromoting ? 'Working' : (primaryActionLabel || 'Document actions')}
                >
                  <ArrowRight size={15} />
                  <ChevronDown size={13} className={`transition-transform ${showPromotionMenu ? 'rotate-180' : ''}`} />
                </button>

                {showPromotionMenu && (
                  <div className="absolute right-0 top-full mt-2 w-64 rounded-2xl border border-gray-200 bg-white py-1.5 shadow-2xl z-[90]">
                    {/* SSOT final action menu (configured per document type) */}
                    {config.finalActionMenu ? (
                      <>{renderConfigFinalActionMenuItems()}</>
                    ) : (
                      <>
                        {/* Billing Submission specific actions */}
                        {doc_type === 'billing_submission' ? (
                          <div data-tour-id="billing-submission-actions">
                            <button
                              type="button"
                              onClick={() => void handleCompleteAndPreview()}
                              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50"
                            >
                              <Eye size={16} className="text-blue-600" />
                              <span className="leading-tight">Preview Billing Submission</span>
                            </button>
                            {lifecycleActions.length > 0 && (
                              <>
                                <div className="mx-2 border-t border-gray-100" />
                                <div className="px-3 py-2">
                                  <span className="text-[10px] font-semibold uppercase text-gray-500">Actions</span>
                                </div>
                                {lifecycleActions.map((action) => (
                                  <button
                                    key={action.id}
                                    type="button"
                                    onClick={() => performLifecycleAction(action)}
                                    disabled={performingActionId !== null}
                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                  >
                                    {action.tone === 'success' && <CheckCircle2 size={16} className="text-green-600" />}
                                    {action.tone === 'danger' && <XCircle size={16} className="text-red-600" />}
                                    {action.tone === 'warning' && <AlertTriangle size={16} className="text-amber-600" />}
                                    {action.tone === 'primary' && <CheckCircle2 size={16} className="text-blue-600" />}
                                    <span>{action.label}</span>
                                  </button>
                                ))}
                              </>
                            )}
                          </div>
                        ) : (
                          <>
                            {/* Complete & Send — convert to EXP then send, or save then send */}
                            <button
                              type="button"
                              data-tour-id="document-send-button"
                              onClick={() => void (hasExpensePromotionTarget ? handleCompleteAndSendConversion() : handleOpenSendModal())}
                              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50"
                            >
                              <Send size={16} className="text-blue-600" />
                              <span className="leading-tight">Complete & Send</span>
                            </button>
                            {/* Complete — convert to EXP (no send), or save then preview */}
                            <button
                              type="button"
                              data-tour-id={hasExpensePromotionTarget ? 'expense-publish' : undefined}
                              onClick={() => void (hasExpensePromotionTarget ? handlePromoteDocument('expense') : handleCompleteAndPreview())}
                              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50"
                            >
                              <Eye size={16} className="text-blue-600" />
                              <span className="leading-tight">Complete {hasExpensePromotionTarget ? 'Expense' : config.labels.title}</span>
                            </button>
                            {canPromote && promotionTargets.length > 0 && !hasExpensePromotionTarget && (
                              <>
                                <div className="mx-2 border-t border-gray-100" />
                                <div className="px-3 py-2">
                                  <span className="text-[10px] font-semibold uppercase text-gray-500">Convert to</span>
                                </div>
                                {promotionTargets.map((target) => (
                                  <button
                                    key={target.type}
                                    type="button"
                                    onClick={() => void handlePromoteDocument(target.type)}
                                    className="flex w-full items-start gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    <span className="text-lg">{target.icon}</span>
                                    <span>
                                      <span className="block">{target.label}</span>
                                      {target.description && (
                                        <span className="mt-0.5 block text-xs text-gray-500">{target.description}</span>
                                      )}
                                    </span>
                                  </button>
                                ))}
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {/* Publish to external accounting system — only for publishable
                        doc types with a connected adapter and approved status */}
                    {renderPublishMenuItems()}
                    {/* Approve & Publish — standard mode DIN → expense → approve → auto-publish */}
                    {renderApproveAndPublishMenuItem()}
                  </div>
                )}
              </div>

              <button
                onClick={handleCloseModal}
                className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors shrink-0"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="rounded-xl bg-white/20 p-2 backdrop-blur">
                <FileText className="text-white" size={20} />
              </div>
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <h2 className="truncate text-base font-bold sm:text-lg">{config.labels.title}</h2>
                  <UserDocsLink docsPath="/total-operations/workflows/finance-essential/creating-documents" label="User guide: Creating documents" className="text-white/80 hover:text-white" />
                  {formData.status && <FinanceStatusBadge status={formData.status} isDark={true} itemType={doc_type} />}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-white/80">
                  {formData.doc_number ? (
                    <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-white/10 px-2 py-0.5">
                      <Hash size={10} />
                      <span className="truncate max-w-[12rem]">
                        {sourceJobNumber ? `${sourceJobNumber} → ${formData.doc_number}` : formData.doc_number}
                      </span>
                    </span>
                  ) : (
                    <span className="rounded-full bg-white/10 px-2 py-0.5">Draft in progress</span>
                  )}
                  {renderSourceBadge()}
                  <ExtractionSourceBadge customFields={document?.custom_fields ?? null} />
                </div>
              </div>
            </div>

            {outstandingRequirements.length > 0 && (
              <div className="hidden md:flex flex-1 min-w-0 justify-end items-center pr-1">
                <div className="flex items-center gap-1.5 rounded-lg bg-amber-500/30 px-2.5 py-1 text-[11px] font-medium text-amber-100 max-w-full">
                  <AlertTriangle size={12} className="shrink-0" />
                  <span className="truncate">{outstandingRequirements.join(' · ')}</span>
                </div>
              </div>
            )}

            <div className="flex shrink-0 items-center gap-2">
              {/* 1. Copy share link */}
              <button
                type="button"
                onClick={() => void handleDocShareLinkAction()}
                disabled={docShareUrlLoading || !document?.id}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-70"
                title={docShareUrl ? 'Copy share link' : 'Generate and copy share link'}
              >
                {docShareUrlLoading ? (
                  <Loader2 size={14} className="sm:w-4 sm:h-4 animate-spin" />
                ) : (
                  <Link2 size={14} className="sm:w-4 sm:h-4" />
                )}
              </button>

              {/* 2. Parent document # + link (if applicable) */}
              {linkedParentDocument && (
                <button
                  onClick={() => {
                    if (linkedParentDocument.id && typeof window !== 'undefined') {
                      window.dispatchEvent(
                        new CustomEvent('openDocumentModal', {
                          detail: {
                            doc_type: linkedParentDocument.doc_type,
                            mode: 'edit',
                            documentId: linkedParentDocument.id,
                          },
                        })
                      );
                    }
                  }}
                  className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
                  title={`Open parent: ${linkedParentDocument.doc_number || 'Parent Document'}`}
                >
                  <ExternalLink size={12} />
                  <span>{linkedParentDocument.doc_number || 'Parent'}</span>
                </button>
              )}

              {/* Other Viewers Badge - Presence Detection */}
              {otherDocViewersCount > 0 && (
                <div
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-500/30 text-amber-100 rounded-lg text-xs font-medium animate-pulse"
                  title={docViewers.map(v => v.user_name).join(', ')}
                >
                  <Users className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">
                    {otherDocViewersCount === 1
                      ? `${docViewers[0]?.user_name || '1 other'} viewing`
                      : `${otherDocViewersCount} others viewing`}
                  </span>
                  <span className="sm:hidden">{otherDocViewersCount}</span>
                </div>
              )}

              {/* 3. Print */}
              <button
                type="button"
                onClick={() => void handleCompleteAndPreview()}
                className="rounded-xl flex items-center gap-1.5 sm:gap-2 font-medium shadow-sm transition-colors touch-manipulation shrink-0 px-3 sm:px-4 py-2 text-xs sm:text-sm bg-white/10 text-white hover:bg-white/20"
                title="Print preview"
              >
                <Printer size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">Print</span>
              </button>

              {/* 4. Save / Saving / Saved */}
              <button
                type="button"
                onClick={() => handleSaveWithRiskCheck(false)}
                disabled={isSaving || !hasUnsavedChanges || isBatchedInvoiceLocked}
                className={`rounded-xl flex items-center gap-1.5 sm:gap-2 font-medium shadow-sm transition-colors touch-manipulation shrink-0 px-3 sm:px-4 py-2 text-xs sm:text-sm ${hasUnsavedChanges && !isBatchedInvoiceLocked
                  ? 'bg-white text-gray-800 hover:bg-gray-100'
                  : 'bg-white/20 text-white/60 cursor-not-allowed'
                  }`}
                title={
                  isBatchedInvoiceLocked
                    ? 'Document is locked by an open invoice batch. Post the batch or create a credit note to make changes.'
                    : isSaving
                      ? 'Saving…'
                      : !hasUnsavedChanges && lastAutoSave
                        ? 'Saved'
                        : 'Save'
                }
              >
                {isSaving ? (
                  <RefreshCw size={14} className="animate-spin sm:w-4 sm:h-4" />
                ) : !hasUnsavedChanges && lastAutoSave ? (
                  <Check size={14} className="sm:w-4 sm:h-4" />
                ) : (
                  <Save size={14} className="sm:w-4 sm:h-4" />
                )}
                <span className="hidden xs:inline">
                  {isBatchedInvoiceLocked
                    ? 'Locked'
                    : isSaving
                      ? 'Saving...'
                      : !hasUnsavedChanges && lastAutoSave
                        ? 'Saved'
                        : 'Save'}
                </span>
              </button>

              {/* 4b. Save as Draft — DIN-only: saves partial progress and closes */}
              {doc_type === 'din' && onSaveAsDraft && (
                <button
                  type="button"
                  onClick={() => void onSaveAsDraft()}
                  disabled={isSavingDraft || isSaving}
                  className="rounded-xl flex items-center gap-1.5 sm:gap-2 font-medium shadow-sm transition-colors touch-manipulation shrink-0 px-3 sm:px-4 py-2 text-xs sm:text-sm bg-white/20 text-white hover:bg-white/30 disabled:opacity-50"
                  title="Save partial progress as draft (e.g., GL allocated but no supplier) and close"
                >
                  {isSavingDraft ? (
                    <Loader2 size={14} className="sm:w-4 sm:h-4 animate-spin" />
                  ) : (
                    <FileText size={14} className="sm:w-4 sm:h-4" />
                  )}
                  <span className="hidden xs:inline">Save Draft</span>
                </button>
              )}

              {/* 5. Final Action Dropdown Button */}
              <div className="relative" data-promotion-menu>
                <button
                  type="button"
                  data-tour-id="document-actions-menu"
                  onClick={() => setShowPromotionMenu(!showPromotionMenu)}
                  disabled={isPromoting || isBatchedInvoiceLocked || isFinalActionMenuGated}
                  className="bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-50 flex items-center gap-1.5 sm:gap-2 font-medium shadow-sm transition-colors touch-manipulation shrink-0 rounded-xl px-3 sm:px-4 py-2 text-xs sm:text-sm"
                  title={
                    isBatchedInvoiceLocked
                      ? 'Document is locked by an open invoice batch.'
                      : isFinalActionMenuGated
                        ? finalActionGateResult.message || 'Required fields missing'
                        : undefined
                  }
                >
                  <ArrowRight size={14} className="sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline">
                    {isBatchedInvoiceLocked
                      ? 'Locked'
                      : isFinalActionMenuGated
                        ? 'Actions'
                        : isPromoting
                          ? 'Working…'
                          : (primaryActionLabel || 'Actions')}
                  </span>
                  <ChevronDown size={14} />
                </button>

                {showPromotionMenu && (
                  <div className="absolute right-0 top-full mt-1 w-60 bg-white rounded-2xl shadow-xl border border-gray-200 py-1 z-50">
                    {/* SSOT final action menu (configured per document type) */}
                    {config.finalActionMenu ? (
                      <>{renderConfigFinalActionMenuItems()}</>
                    ) : (
                      <>
                        {/* Billing Submission specific actions */}
                        {doc_type === 'billing_submission' ? (
                          <div data-tour-id="billing-submission-actions">
                            <button
                              type="button"
                              onClick={() => void handleCompleteAndPreview()}
                              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50"
                            >
                              <Eye size={16} className="text-blue-600" />
                              <span>Preview Billing Submission</span>
                            </button>
                            {lifecycleActions.length > 0 && (
                              <>
                                <div className="mx-2 border-t border-gray-100" />
                                <div className="px-3 py-2 border-b border-gray-100">
                                  <span className="text-xs font-semibold text-gray-500">Actions</span>
                                </div>
                                {lifecycleActions.map((action) => (
                                  <button
                                    key={action.id}
                                    type="button"
                                    onClick={() => performLifecycleAction(action)}
                                    disabled={performingActionId !== null}
                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                  >
                                    {action.tone === 'success' && <CheckCircle2 size={16} className="text-green-600" />}
                                    {action.tone === 'danger' && <XCircle size={16} className="text-red-600" />}
                                    {action.tone === 'warning' && <AlertTriangle size={16} className="text-amber-600" />}
                                    {action.tone === 'primary' && <CheckCircle2 size={16} className="text-blue-600" />}
                                    <span>{action.label}</span>
                                  </button>
                                ))}
                              </>
                            )}
                          </div>
                        ) : (
                          <>
                            {/* Complete & Send — convert to EXP then send, or save then send */}
                            <button
                              type="button"
                              data-tour-id="document-send-button"
                              onClick={() => void (hasExpensePromotionTarget ? handleCompleteAndSendConversion() : handleOpenSendModal())}
                              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50"
                            >
                              <Send size={16} className="text-blue-600" />
                              <span className="leading-tight">Complete & Send</span>
                            </button>
                            {/* Complete — convert to EXP (no send), or save then preview */}
                            <button
                              type="button"
                              data-tour-id={hasExpensePromotionTarget ? 'expense-publish' : undefined}
                              onClick={() => void (hasExpensePromotionTarget ? handlePromoteDocument('expense') : handleCompleteAndPreview())}
                              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50"
                            >
                              <Eye size={16} className="text-blue-600" />
                              <span className="leading-tight">Complete {hasExpensePromotionTarget ? 'Expense' : config.labels.title}</span>
                            </button>
                            {canPromote && promotionTargets.length > 0 && !hasExpensePromotionTarget && (
                              <>
                                <div className="mx-2 border-t border-gray-100" />
                                <div className="px-3 py-2">
                                  <span className="text-[10px] font-semibold uppercase text-gray-500">Convert to</span>
                                </div>
                                {promotionTargets.map((target) => (
                                  <button
                                    key={target.type}
                                    type="button"
                                    onClick={() => void handlePromoteDocument(target.type)}
                                    className="flex w-full items-start gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    <span className="text-lg">{target.icon}</span>
                                    <span>
                                      <span className="block">{target.label}</span>
                                      {target.description && (
                                        <span className="mt-0.5 block text-xs text-gray-500">{target.description}</span>
                                      )}
                                    </span>
                                  </button>
                                ))}
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {/* Publish to external accounting system — only for publishable
                        doc types with a connected adapter and approved status */}
                    {renderPublishMenuItems()}
                    {/* Approve & Publish — standard mode DIN → expense → approve → auto-publish */}
                    {renderApproveAndPublishMenuItem()}
                  </div>
                )}
              </div>

              {/* Make Recurring - Create recurring schedule from this document (for invoices and expenses) */}
              {document?.id && ['invoice', 'expense'].includes(doc_type) && formData.contact_id && !document.parent_document_id && (
                <button
                  onClick={() => setShowRecurringModal(true)}
                  className="hidden sm:flex px-3 sm:px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium shadow-sm transition-colors touch-manipulation"
                  style={{ backgroundColor: primaryColor }}
                  title="Create recurring schedule from this document"
                >
                  <RefreshCw size={14} className="sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Make Recurring</span>
                </button>
              )}

              <button
                onClick={handleCloseModal}
                className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors shrink-0"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Side Panel Toggle — 3-way: none | photos | details (second row, right-aligned).
            AI is now triggered from the footer AI sub-menu button. */}
        <div className="hidden lg:flex lg:w-[400px] xl:w-[430px] 2xl:w-[460px] flex-shrink-0 p-0 m-0 ml-auto lg:-mr-6 lg:-mb-5">
          <SidePanelToggle
            mode={sidePanelMode}
            onChange={(mode) => setSidePanelMode(mode)}
            isDark={true}
            hideAI={true}
          />
        </div>
      </div>

      {/* Mobile collapsed header summary */}
      {isCompactMobileDoc && !isMobileHeaderExpanded && activeTab === 'items' && (
        <div className={`border-b px-3 py-2.5 ${isDarkMode ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'
          }`}>
          <button
            type="button"
            onClick={() => {
              setActiveTab('details');
              setIsMobileHeaderExpanded(true);
            }}
            className="w-full flex items-center justify-between gap-3 text-left"
          >
            <div className="min-w-0">
              <div className={`truncate text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {mobileHeaderSummary}
              </div>
              <div className={`mt-0.5 flex flex-wrap items-center gap-2 text-[11px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <span>{formData.issued_at || 'No date selected'}</span>
                <span>•</span>
                <span className="font-semibold">{currencySymbol} {formatCurrency(formData.grand_total ?? 0)}</span>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
              }`}>
              Details
              <ChevronDown size={12} />
            </span>
          </button>
        </div>
      )}
    </>
  );
}

export default DocumentModalHeader;
